import { Text } from "components";
import React, { useMemo } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import CreatableSelect from "react-select/creatable";

const Pagination = ({
  count = 0,
  page = 1,
  setPage,
  setRecordPerPage,
  recordPerPage = 50,
  setLoading,
  setData,
}) => {
  const pageSizeInput = [50, 100, 200, 300, 500, 1000];

  const options = useMemo(
    () =>
      pageSizeInput.map((size) => ({
        label: size,
        value: size,
      })),
    []
  );

  const safeCount = Number(count) || 0;
  const safeRecordPerPage = Number(recordPerPage) || 50;

  const roundedTotalPages = Math.max(
    1,
    Math.ceil(safeCount / safeRecordPerPage)
  );

  const selectedOption =
    options.find((option) => option.value === safeRecordPerPage) || {
      label: safeRecordPerPage,
      value: safeRecordPerPage,
    };

  const handlePageChange = (newPage) => {
    let updatedPage = Number(newPage);

    if (Number.isNaN(updatedPage) || updatedPage < 1) {
      updatedPage = 1;
    }

    if (updatedPage > roundedTotalPages) {
      updatedPage = roundedTotalPages;
    }

    if (updatedPage !== page) {
      setLoading(true);
      setData([]);
      setPage(updatedPage);
    }
  };

  const decrementCount = () => {
    if (page <= 1) return;

    setLoading(true);
    setData([]);
    setPage(page - 1);
  };

  const incrementCount = () => {
    if (page >= roundedTotalPages) return;

    setLoading(true);
    setData([]);
    setPage(page + 1);
  };

  const handleChangePageSize = (selectedOption) => {
    if (!selectedOption) return;

    setLoading(true);
    setData([]);
    setRecordPerPage(selectedOption.value);
    setPage(1);
  };

  return (
    <div className="flex items-center justify-end p-1">
      <Text className="child-text text-gray-700 pr-1">Page Size</Text>

      <CreatableSelect
        options={options}
        value={selectedOption}
        className="w-20"
        menuPlacement="top"
        onChange={handleChangePageSize}
        isClearable={false}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: "28px",
            height: "28px",
          }),
          valueContainer: (base) => ({
            ...base,
            height: "28px",
            padding: "0 6px",
          }),
          input: (base) => ({
            ...base,
            margin: "0",
            padding: "0",
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: "28px",
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: "4px",
          }),
          clearIndicator: (base) => ({
            ...base,
            padding: "4px",
          }),
          singleValue: (base) => ({
            ...base,
            fontSize: "12px",
          }),
        }}
      />

      <button
        onClick={decrementCount}
        disabled={page <= 1}
        className={`p-1 ${page <= 1
          ? "cursor-not-allowed opacity-50"
          : "hover:text-blue-600"
          }`}
      >
        <IoIosArrowBack />
      </button>

      <div className="flex items-center space-x-2">
        <span className="child-text text-gray-700">Page</span>

        <input
          type="number"
          min={1}
          max={roundedTotalPages}
          value={page}
          onChange={(e) => {
            const value = e.target.value;

            if (value === "") {
              setPage("");
              return;
            }

            const number = Number(value);

            if (!Number.isNaN(number)) {
              setPage(number);
            }
          }}
          onBlur={(e) => handlePageChange(e.target.value)}
          className="w-8 h-6 px-0 py-1 child-text rounded-md border border-gray-300 text-center focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div className="flex items-center space-x-2">
        <span className="child-text text-gray-700 pl-1">of</span>

        <input
          type="number"
          value={roundedTotalPages}
          readOnly
          className="w-8 h-6 px-0 py-1 child-text text-center border border-gray-300 rounded bg-gray-100"
        />
      </div>

      <button
        onClick={incrementCount}
        disabled={page >= roundedTotalPages}
        className={`p-1 ${page >= roundedTotalPages
          ? "cursor-not-allowed opacity-50"
          : "hover:text-blue-600"
          }`}
      >
        <IoIosArrowForward />
      </button>
    </div>
  );
};

export default Pagination;